/**
 * NayaxDiagnostic — Phase 87bb (Feb 2026)
 *
 * Page READ-ONLY pour comprendre quelles tx Nayax remontent et leur destin :
 *  - synced : déjà en base
 *  - before_cutoff : avant la date de coupure
 *  - empty_name : nom vide/None/Unknown (refund/void)
 *  - unmapped : produit Nayax non trouvé en base → propose alias + bouton 1 clic
 *  - insufficient_stock : matché mais pas de stock dispo
 *  - ready : sera créée à la prochaine sync
 *
 * UI : un select distributeur en haut, des cards de récap, et un tableau.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { fmtDateTime, fmtNum } from "../lib/format";
import { Activity, AlertCircle, Check, Clock, Loader2, RefreshCw, Search, Tag, X, Zap, Archive, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";
import NayaxSalesAudit from "../components/nayax/NayaxSalesAudit";
import NayaxHistoryProbe from "../components/nayax/NayaxHistoryProbe";

const STATUS_META = {
  synced:              { label: "Synchronisée",   color: "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" },
  ready:               { label: "Prête à sync",   color: "bg-[#E8E4F8] text-[#5847A6] border-[#C8C0E8]" },
  unmapped:            { label: "Non mappée",     color: "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]" },
  insufficient_stock:  { label: "Stock insuffisant", color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  before_cutoff:       { label: "Avant cutoff",   color: "bg-[#F4F4F0] text-[#575A57] border-[#C8C8C0]" },
  empty_name:          { label: "Refund / Void",  color: "bg-[#F4F4F0] text-[#A3A6A3] border-[#C8C8C0]" },
  ignored:             { label: "Enterrée",       color: "bg-[#EFEFEA] text-[#7A7A75] border-[#D0D0CA]" },
};

export default function NayaxDiagnostic() {
  const [distributors, setDistributors] = useState([]);
  const [selectedDistId, setSelectedDistId] = useState("");
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState({}); // {tx_id: bool}
  // Phase 87be — Enterrer / réactiver une tx
  const [burying, setBurying] = useState({}); // {tx_id: bool}
  const [bulkBurying, setBulkBurying] = useState(false);
  const confirm = useConfirm();

  // Charger les distributeurs AUTOMATIQUE avec nayax_machine_id
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/distributors");
        const auto = (r.data || []).filter((d) => d.type === "AUTOMATIQUE" && d.nayax_machine_id);
        setDistributors(auto);
        if (auto.length > 0 && !selectedDistId) setSelectedDistId(auto[0].id);
      } catch {
        toast.error("Erreur chargement distributeurs");
      }
    })();
  }, []);

  const loadDiagnostic = async () => {
    if (!selectedDistId) return;
    setLoading(true);
    setDiagnostic(null);
    try {
      const { data } = await api.get(`/nayax/diagnostic/${selectedDistId}?limit=50`);
      setDiagnostic(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur diagnostic");
    } finally {
      setLoading(false);
    }
  };

  // Recharge à chaque changement de distributeur
  useEffect(() => { if (selectedDistId) loadDiagnostic(); }, [selectedDistId]);

  const txs = diagnostic?.transactions || [];
  const summary = diagnostic?.summary || {};

  const filteredTxs = useMemo(() => {
    let out = txs;
    if (filter !== "all") out = out.filter((t) => t.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((t) =>
        (t.raw_product_name || "").toLowerCase().includes(q) ||
        (t.transaction_id || "").toLowerCase().includes(q) ||
        (t.matched_product?.name || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [txs, filter, search]);

  const addAlias = async (tx, productId) => {
    setAdding((s) => ({ ...s, [tx.transaction_id]: true }));
    try {
      const { data } = await api.post("/nayax/diagnostic/add-alias", {
        product_id: productId,
        alias: tx.raw_product_name,
      });
      if (data.already_present) {
        toast.info(`Alias « ${tx.raw_product_name} » déjà existant`);
      } else {
        toast.success(`Alias ajouté au produit (${data.total_aliases} aliases au total)`);
      }
      // Recharge pour voir le nouveau statut (devrait passer en `ready`)
      await loadDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur ajout alias");
    } finally {
      setAdding((s) => ({ ...s, [tx.transaction_id]: false }));
    }
  };

  // Phase 87be — Enterrer une tx (ignored). Elle ne sera plus jamais resynchronisée
  // ni affichée en erreur. Réversible via le bouton "Réactiver" sur les lignes ignored.
  const buryTx = async (tx) => {
    setBurying((s) => ({ ...s, [tx.transaction_id]: true }));
    try {
      const reason = `${tx.status} · ${tx.raw_product_name || "—"}`;
      const { data } = await api.post("/nayax/ignored-txs", {
        transaction_id: tx.transaction_id,
        reason,
      });
      if (data.already_ignored) {
        toast.info("Tx déjà enterrée");
      } else {
        toast.success(`Tx #${tx.transaction_id} enterrée — elle ne réapparaîtra plus.`);
      }
      await loadDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBurying((s) => ({ ...s, [tx.transaction_id]: false }));
    }
  };

  const unburyTx = async (tx) => {
    setBurying((s) => ({ ...s, [tx.transaction_id]: true }));
    try {
      await api.delete(`/nayax/ignored-txs/${encodeURIComponent(tx.transaction_id)}`);
      toast.success("Tx réactivée — elle sera retentée à la prochaine sync.");
      await loadDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBurying((s) => ({ ...s, [tx.transaction_id]: false }));
    }
  };

  // Phase 87be — Enterrer en masse toutes les erreurs visibles (filtre actif)
  const buryAllVisible = async () => {
    const targets = filteredTxs.filter((t) => ["unmapped", "insufficient_stock"].includes(t.status));
    if (targets.length === 0) {
      toast.info("Aucune tx en erreur visible à enterrer");
      return;
    }
    const ok = await confirm({
      title: `Enterrer ${targets.length} tx en erreur ?`,
      description: "Ces tx ne réapparaîtront plus dans le diagnostic ni dans les logs d'erreur. Chaque tx peut être réactivée individuellement en filtrant sur le statut « Enterrée ».",
      confirmText: `Enterrer ${targets.length} tx`,
    });
    if (!ok) return;
    setBulkBurying(true);
    try {
      const { data } = await api.post("/nayax/ignored-txs/bulk", {
        transaction_ids: targets.map((t) => t.transaction_id),
        reason: "Bulk-bury depuis diagnostic",
      });
      toast.success(`${data.added} tx enterrées${data.already_ignored > 0 ? ` (${data.already_ignored} l'étaient déjà)` : ""}`);
      await loadDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBulkBurying(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="nayax-diagnostic-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A] flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#5847A6]" /> Diagnostic Nayax
          </h1>
          <p className="text-sm text-[#575A57]">
            Diagnostic temps réel par distributeur · Audit comparatif Nayax vs AgroManage par date · Aliases 1 clic
          </p>
        </div>
      </div>

      <NayaxHistoryProbe />

      <Tabs defaultValue="audit" className="w-full">
        <TabsList className="grid w-full md:w-[480px] grid-cols-2">
          <TabsTrigger value="audit" data-testid="tab-audit">
            <Search className="w-4 h-4 mr-2" /> Audit ventes par date
          </TabsTrigger>
          <TabsTrigger value="realtime" data-testid="tab-realtime">
            <Activity className="w-4 h-4 mr-2" /> Diagnostic temps réel
          </TabsTrigger>
        </TabsList>

        <TabsContent value="audit" className="mt-6">
          <NayaxSalesAudit />
        </TabsContent>

        <TabsContent value="realtime" className="mt-6">
          <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-[#575A57]">
            Vue read-only des 50 dernières transactions remontées par l&apos;API Nayax · statut de chaque tx · ajout d&apos;alias en 1 clic
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadDiagnostic}
          disabled={loading || !selectedDistId}
          data-testid="reload-diagnostic-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
        {/* Phase 87be — Enterrer en masse les tx en erreur visibles */}
        {diagnostic && !loading && (
          (() => {
            const buryableCount = filteredTxs.filter((t) => ["unmapped", "insufficient_stock"].includes(t.status)).length;
            if (buryableCount === 0) return null;
            return (
              <Button
                variant="outline"
                onClick={buryAllVisible}
                disabled={bulkBurying}
                className="border-[#8C6B2F]/40 text-[#8C6B2F] hover:bg-[#FBF4E9]"
                data-testid="bury-all-visible-btn"
              >
                {bulkBurying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                Enterrer {buryableCount} erreur{buryableCount > 1 ? "s" : ""}
              </Button>
            );
          })()
        )}
      </div>

      {/* Sélecteur distributeur */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#575A57]">Distributeur :</span>
            <Select value={selectedDistId} onValueChange={setSelectedDistId}>
              <SelectTrigger className="w-[400px]" data-testid="diag-dist-select">
                <SelectValue placeholder="Choisir un distributeur Nayax" />
              </SelectTrigger>
              <SelectContent>
                {distributors.length === 0 ? (
                  <SelectItem value="__empty__" disabled>
                    Aucun distributeur AUTOMATIQUE avec nayax_machine_id
                  </SelectItem>
                ) : (
                  distributors.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} · nayax #{d.nayax_machine_id}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {diagnostic?.cutoff_date && (
              <Badge variant="outline" className="ml-2" data-testid="diag-cutoff">
                <Clock className="w-3 h-3 mr-1" /> Cutoff : {diagnostic.cutoff_date}
              </Badge>
            )}
            {diagnostic?.transactions_received != null && (
              <Badge variant="outline" data-testid="diag-total">
                {diagnostic.transactions_received} tx remontées par l&apos;API
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-[#5847A6]" data-testid="diag-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Récupération depuis l&apos;API Nayax…</span>
        </div>
      )}

      {/* Erreur (pas de machine configurée par exemple) */}
      {diagnostic?.error && (
        <div className="bg-[#FBEEEA] border border-[#E8B0A0] rounded p-4 text-sm flex items-start gap-2" data-testid="diag-error">
          <AlertCircle className="w-5 h-5 text-[#A04E2C] flex-shrink-0 mt-0.5" />
          <div className="text-[#A04E2C]">{diagnostic.error}</div>
        </div>
      )}

      {/* Cards de résumé */}
      {diagnostic && !diagnostic.error && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="diag-summary">
          {Object.entries(STATUS_META).map(([key, meta]) => {
            const count = summary[key] || 0;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(filter === key ? "all" : key)}
                className={`border rounded p-3 text-left transition ${meta.color} ${filter === key ? "ring-2 ring-offset-1 ring-[#1F4E3D]" : "hover:opacity-90"}`}
                data-testid={`diag-filter-${key}`}
              >
                <div className="text-xs uppercase tracking-wider opacity-75">{meta.label}</div>
                <div className="text-2xl font-bold mt-1">{count}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Barre de recherche + filtre courant */}
      {diagnostic && !diagnostic.error && !loading && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A6A3]" />
            <input
              type="text"
              placeholder="Filtrer par nom Nayax, ID tx, produit AM…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 w-full border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F4E3D]"
              data-testid="diag-search"
            />
          </div>
          {filter !== "all" && (
            <Button variant="ghost" size="sm" onClick={() => setFilter("all")} data-testid="diag-clear-filter">
              <X className="w-3 h-3 mr-1" /> Filtre : {STATUS_META[filter]?.label}
            </Button>
          )}
        </div>
      )}

      {/* Table tx */}
      {diagnostic && !diagnostic.error && !loading && (
        <Card>
          <CardContent className="pt-6">
            {filteredTxs.length === 0 ? (
              <div className="text-center text-sm text-[#A3A6A3] py-8" data-testid="diag-empty">
                Aucune transaction ne correspond au filtre.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date / heure</TableHead>
                    <TableHead>Nom Nayax</TableHead>
                    <TableHead className="text-right">Qté</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Détail · Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTxs.map((tx) => (
                    <TableRow key={tx.transaction_id} data-testid={`diag-row-${tx.transaction_id}`}>
                      <TableCell className="text-xs font-mono whitespace-nowrap">
                        {fmtDateTime(tx.sold_at)}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{tx.raw_product_name || <em className="text-[#A3A6A3]">(vide)</em>}</div>
                        {tx.matched_product && (
                          <div className="text-[10px] text-[#1F4E3D] mt-0.5">
                            → {tx.matched_product.name}
                          </div>
                        )}
                        <div className="text-[10px] text-[#A3A6A3] font-mono mt-0.5">#{tx.transaction_id}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtNum(tx.quantity)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {tx.amount != null ? `${Number(tx.amount).toFixed(2)} CHF` : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_META[tx.status]?.color || ""} variant="outline" data-testid={`diag-status-${tx.transaction_id}`}>
                          {STATUS_META[tx.status]?.label || tx.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-[#575A57]">
                        <div>{tx.details}</div>
                        {/* Suggestions d'alias pour tx unmapped */}
                        {tx.status === "unmapped" && tx.suggestions && tx.suggestions.length > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wider text-[#A3A6A3]">Ajouter alias à :</span>
                            {tx.suggestions.map((s) => (
                              <Button
                                key={s.id}
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => addAlias(tx, s.id)}
                                disabled={!!adding[tx.transaction_id]}
                                data-testid={`diag-add-alias-${tx.transaction_id}-${s.id}`}
                              >
                                {adding[tx.transaction_id] ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Tag className="w-3 h-3 mr-1" />
                                )}
                                {s.name}
                                <span className="ml-1 text-[#A3A6A3]">{Math.round(s.ratio * 100)}%</span>
                              </Button>
                            ))}
                          </div>
                        )}
                        {tx.status === "unmapped" && (!tx.suggestions || tx.suggestions.length === 0) && (
                          <div className="mt-1 text-[10px] text-[#A04E2C]">
                            Aucune suggestion. Créez d&apos;abord le produit dans /produits, puis revenez.
                          </div>
                        )}
                        {/* Phase 87be — Bouton « Enterrer » sur les erreurs résiduelles */}
                        {["unmapped", "insufficient_stock"].includes(tx.status) && (
                          <div className="mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => buryTx(tx)}
                              disabled={!!burying[tx.transaction_id]}
                              className="h-7 text-[11px] text-[#7A7A75] hover:text-[#8C6B2F] hover:bg-[#FBF4E9]"
                              data-testid={`diag-bury-${tx.transaction_id}`}
                              title="Enterrer définitivement cette tx — elle ne réapparaîtra plus en erreur"
                            >
                              {burying[tx.transaction_id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Archive className="w-3 h-3 mr-1" />}
                              Enterrer
                            </Button>
                          </div>
                        )}
                        {tx.status === "ignored" && (
                          <div className="mt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => unburyTx(tx)}
                              disabled={!!burying[tx.transaction_id]}
                              className="h-7 text-[11px] text-[#5847A6] hover:bg-[#E8E4F8]"
                              data-testid={`diag-unbury-${tx.transaction_id}`}
                              title="Réactiver cette tx (elle sera retentée à la prochaine sync)"
                            >
                              {burying[tx.transaction_id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                              Réactiver
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* État initial — pas encore de diagnostic */}
      {!loading && !diagnostic && distributors.length > 0 && (
        <div className="text-center text-sm text-[#A3A6A3] py-8">
          Sélectionnez un distributeur pour lancer le diagnostic.
        </div>
      )}
      {!loading && distributors.length === 0 && (
        <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-4 text-sm text-[#8C6B2F]" data-testid="diag-no-dist">
          <Zap className="w-4 h-4 inline mr-1" />
          Aucun distributeur AUTOMATIQUE n&apos;a de <code>nayax_machine_id</code> configuré. Allez dans <strong>Distributeurs</strong> → édition → renseignez l&apos;ID machine Nayax pour activer la sync.
        </div>
      )}

      {/* Légende statuts */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-xs text-[#575A57] space-y-2">
            <div className="font-medium text-sm text-[#1A1C1A]">Légende des statuts</div>
            <ul className="space-y-1 pl-4 list-disc">
              <li><strong>Synchronisée</strong> : la vente est déjà en base, ne sera pas re-traitée (idempotence par TransactionID).</li>
              <li><strong>Prête à sync</strong> : pas encore en base, sera créée au prochain passage du scheduler (toutes les 15 min) ou via « Forcer sync Nayax » dans /ventes.</li>
              <li><strong>Non mappée</strong> : le nom Nayax ne correspond à aucun produit AM. Cliquez sur un produit suggéré pour ajouter l&apos;alias en 1 clic — la vente sera ensuite synchronisée.</li>
              <li><strong>Stock insuffisant</strong> : produit matché mais aucun lot disponible dans ce distributeur. Refaire un approvisionnement.</li>
              <li><strong>Avant cutoff</strong> : tx antérieure à la date de coupure configurée dans /parametres → ignorée silencieusement.</li>
              <li><strong>Refund / Void</strong> : nom Nayax vide / « None » / « Unknown(...) » — annulation ou tx invalide, ignorée silencieusement.</li>
              <li><strong>Enterrée</strong> : tx volontairement blacklistée par l&apos;opérateur (bouton « Enterrer »). Skip permanent à chaque sync, n&apos;apparaît plus dans les rapports. Réversible via « Réactiver ».</li>
            </ul>
          </div>
        </CardContent>
      </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
